import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const anchorLinks = [];
const routeLinks = [
  { path: "/", labelKey: "nav.home", en: "Home" },
  { path: "/services", labelKey: "nav.services", en: "Services" },
  { path: "/experiences", labelKey: "nav.experiences", en: "Experiences" },
  { path: "/concept-store", labelKey: "nav.conceptStore", en: "Concept Store" },
  { path: "/recent-projects", labelKey: "nav.recentProjects", en: "Recent Projects" },
  { path: "/about", labelKey: "nav.about", en: "About" },
  { path: "/contact", labelKey: "nav.contact", en: "Contact" },
];

const DecoStar = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M12 2 L14 10 L22 12 L14 14 L12 22 L10 14 L2 12 L10 10 Z"
      fill="var(--lu-gold)"
    />
  </svg>
);

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, setLang, t } = useLanguage();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Handle "/#contact" style navigation after route change
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
    }
  }, [location]);

  const goToAnchor = (id) => {
    setOpen(false);
    if (id === "contact") {
      navigate("/book-a-call");
      return;
    }
    if (location.pathname === "/") {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/#${id}`);
    }
  };

  // On non-home routes the page background is ivory — the nav needs a
  // solid-enough dark background so the light nav text remains legible.
  // (Tailwind's `bg-[var(--x)]/90` opacity modifier silently fails on hex
  // CSS variables, so we set the background inline as an rgba value.)
  const isHome = location.pathname === "/";
  const navSolid = !isHome || scrolled;
  const navStyle = {
    background: navSolid ? "rgba(10, 10, 10, 0.92)" : "rgba(10, 10, 10, 0.35)",
    boxShadow: navSolid ? "0 10px 30px -15px rgba(0,0,0,0.6)" : "none",
  };

  return (
    <header
      className="fixed top-0 inset-x-0 z-50 nav-pill transition-all duration-500"
      style={navStyle}
      data-testid="site-nav"
    >
      <div className="hidden lg:flex items-center justify-center gap-3 pt-3 pb-1">
        <span className="h-px w-24 bg-[var(--lu-gold)]/70" />
        <DecoStar />
        <span className="h-px w-24 bg-[var(--lu-gold)]/70" />
      </div>

      <div className="max-w-7xl mx-auto flex flex-col items-center px-6 lg:px-10 pt-1 pb-2 lg:pb-3">
        <div className="flex w-full items-center justify-between lg:hidden">
          <Link
            to="/"
            onClick={() => setOpen(false)}
            className="flex flex-col items-start leading-none"
            data-testid="nav-brand"
          >
            <span className="font-serif text-[24px] tracking-[0.3em] text-[var(--lu-gold)]">
              LUMIÈRE
            </span>
            <span className="font-micro text-[8px] text-ivory-70 mt-0.5 tracking-[0.4em]">
              ENTERTAINMENT
            </span>
          </Link>
          <button
            className="text-[var(--lu-ivory)]"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Open menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        <Link
          to="/"
          className="hidden lg:flex flex-col items-center leading-none"
          data-testid="nav-brand"
        >
          <span
            className="font-serif text-[30px] tracking-[0.55em] text-[var(--lu-gold)]"
            style={{ fontWeight: 500 }}
          >
            LUMIÈRE
          </span>
          <span className="font-serif tracking-[0.5em] text-[9px] text-ivory-80 mt-1">
            ENTERTAINMENT
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 mt-3">
          {anchorLinks.map((l, i) => (
            <React.Fragment key={l.id}>
              <button
                onClick={() => goToAnchor(l.id)}
                className="font-serif tracking-[0.35em] text-[11px] text-[var(--lu-ivory)] hover:text-[var(--lu-gold)] transition-colors px-3 py-1 uppercase"
                data-testid={`nav-link-${l.id}`}
              >
                {l.label}
              </button>
              <span className="deco-diamond" style={{ margin: "0 6px" }} />
            </React.Fragment>
          ))}
          {routeLinks.map((l, i) => (
            <React.Fragment key={l.path}>
              <Link
                to={l.path}
                className="font-serif tracking-[0.35em] text-[11px] text-[var(--lu-ivory)] hover:text-[var(--lu-gold)] transition-colors px-3 py-1 uppercase"
                data-testid={`nav-link-${l.path === "/" ? "/" : l.path.slice(1)}`}
              >
                {l.en}
              </Link>
              {i < routeLinks.length - 1 && (
                <span className="deco-diamond" style={{ margin: "0 6px" }} />
              )}
            </React.Fragment>
          ))}
          <span className="mx-4 h-4 w-px bg-[var(--lu-gold)]/60" />
          <button
            onClick={() => goToAnchor("contact")}
            className="btn-gold-outline text-[10px] px-5 py-2"
            data-testid="nav-cta"
          >
            {"BOOK"}
          </button>

          {/* Language switcher removed — site served in English only */}
        </nav>
      </div>

      <div className="hidden lg:block deco-line" />

      {open && (
        <div className="lg:hidden bg-[var(--lu-black)]/95 border-t border-[var(--lu-gold)]/30">
          <div className="flex flex-col px-6 py-6 gap-4">
            {anchorLinks.map((l) => (
              <button
                key={l.id}
                onClick={() => goToAnchor(l.id)}
                className="text-left font-serif uppercase tracking-[0.3em] text-[12px] text-[var(--lu-ivory)] hover:text-[var(--lu-gold)]"
                data-testid={`nav-mobile-link-${l.id}`}
              >
                {l.label}
              </button>
            ))}
            {routeLinks.map((l) => (
              <Link
                key={l.path}
                to={l.path}
                onClick={() => setOpen(false)}
                className="text-left font-serif uppercase tracking-[0.3em] text-[12px] text-[var(--lu-ivory)] hover:text-[var(--lu-gold)]"
                data-testid={`nav-mobile-link-${l.path === "/" ? "/" : l.path.slice(1)}`}
              >
                {l.en}
              </Link>
            ))}
            <button
              onClick={() => goToAnchor("contact")}
              className="text-left font-serif uppercase tracking-[0.3em] text-[12px] text-[var(--lu-gold)]"
              data-testid="nav-mobile-link-contact"
            >
              {"BOOK"}
            </button>

            {/* Mobile language switcher removed — site served in English only */}
          </div>
        </div>
      )}
    </header>
  );
}
